import { auth } from '../config/firebase'
import { normalizeCountryCode } from '../utils/countryCodes'
import { errorFromApi } from '../utils/customerError'
import printedCheckout from '../config/printedCheckout.json'

const {
  PRINTED_CHECKOUT_ENABLED,
  PRINTED_CHECKOUT_PAUSED_MESSAGE
} = printedCheckout

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? '/api' : 'http://localhost:3003/api')

async function getAuthHeaders() {
  const currentUser = auth.currentUser
  if (!currentUser) {
    throw new Error('Please sign in to place an order.')
  }
  let token
  try {
    token = await currentUser.getIdToken()
  } catch (cause) {
    const err = new Error('Your session expired. Please sign in again.')
    err.code = 'AUTH_REQUIRED'
    err.cause = cause
    throw err
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
}

async function postJson(path, body) {
  const headers = await getAuthHeaders()
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  })

  let payload = {}
  try {
    payload = await response.json()
  } catch {
    // non-JSON error body
  }

  if (!response.ok) {
    throw errorFromApi(payload, response.status, 'Something went wrong. Please try again.')
  }

  return payload
}

export function buildShippingAddressFromUser(user) {
  const name =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.name?.trim() ||
    user?.email?.split('@')[0] ||
    ''

  return {
    name,
    street: String(user?.address || '').trim(),
    city: String(user?.city || '').trim(),
    state: String(user?.state || '').trim(),
    zipCode: String(user?.zipCode || '').trim(),
    country: normalizeCountryCode(user?.country, 'US'),
    email: String(user?.email || '').trim(),
    phone: String(user?.phone || '').trim()
  }
}

/** Pre-fill inline checkout form from user profile + order size. */
export function buildFulfillmentFormFromUser(user, orderDetails = {}) {
  const shipping = buildShippingAddressFromUser(user)
  return {
    name: shipping.name,
    email: shipping.email,
    phone: shipping.phone,
    street: shipping.street,
    city: shipping.city,
    state: shipping.state,
    zipCode: shipping.zipCode,
    country: 'US',
    height: user?.height != null ? String(user.height) : '',
    chest: user?.chest != null ? String(user.chest) : '',
    waist: user?.waist != null ? String(user.waist) : '',
    hips: user?.hips != null ? String(user.hips) : '',
    inseam: user?.inseam != null ? String(user.inseam) : '',
    shoulder: user?.shoulder != null ? String(user.shoulder) : '',
    sleeve: user?.sleeve != null ? String(user.sleeve) : '',
    size: orderDetails.size || 'M'
  }
}

export function buildShippingAddressFromFulfillment(fulfillment) {
  return {
    name: String(fulfillment.name || '').trim(),
    street: String(fulfillment.street || '').trim(),
    city: String(fulfillment.city || '').trim(),
    state: String(fulfillment.state || '').trim(),
    zipCode: String(fulfillment.zipCode || '').trim(),
    country: 'US',
    email: String(fulfillment.email || '').trim(),
    phone: String(fulfillment.phone || '').trim()
  }
}

export function buildMeasurementsFromFulfillment(fulfillment, orderDetails = {}) {
  const num = (value) => {
    const n = Number(value)
    return Number.isFinite(n) && n > 0 ? n : undefined
  }

  return {
    size: orderDetails.size || fulfillment.size || 'M',
    height: num(fulfillment.height),
    chest: num(fulfillment.chest),
    waist: num(fulfillment.waist),
    hips: num(fulfillment.hips),
    inseam: num(fulfillment.inseam),
    shoulder: num(fulfillment.shoulder),
    sleeve: num(fulfillment.sleeve),
    units: 'imperial'
  }
}

export function parseNameForProfile(fullName) {
  const trimmed = String(fullName || '').trim()
  const parts = trimmed.split(/\s+/).filter(Boolean)
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' ') || ''
  }
}

/** Allowed Firestore profile fields saved from inline checkout. */
export function buildProfilePatchFromFulfillment(fulfillment) {
  const { firstName, lastName } = parseNameForProfile(fulfillment.name)
  const numOrEmpty = (value) => {
    const n = Number(value)
    return Number.isFinite(n) && n > 0 ? n : value === '' || value == null ? '' : value
  }

  return {
    firstName,
    lastName,
    phone: String(fulfillment.phone || '').trim(),
    address: String(fulfillment.street || '').trim(),
    city: String(fulfillment.city || '').trim(),
    state: String(fulfillment.state || '').trim(),
    zipCode: String(fulfillment.zipCode || '').trim(),
    country: normalizeCountryCode(fulfillment.country, 'US'),
    height: numOrEmpty(fulfillment.height),
    chest: numOrEmpty(fulfillment.chest),
    waist: numOrEmpty(fulfillment.waist),
    hips: numOrEmpty(fulfillment.hips),
    inseam: numOrEmpty(fulfillment.inseam),
    shoulder: numOrEmpty(fulfillment.shoulder),
    sleeve: numOrEmpty(fulfillment.sleeve)
  }
}

export function validateShippingAddress(shippingAddress) {
  const missing = []
  if (!shippingAddress.name) missing.push('name')
  if (!shippingAddress.street) missing.push('street')
  if (!shippingAddress.city) missing.push('city')
  if (!shippingAddress.state) missing.push('state')
  if (!shippingAddress.zipCode) missing.push('zipCode')
  if (!shippingAddress.country) missing.push('country')
  if (!shippingAddress.email) missing.push('email')
  return missing
}

const REQUIRED_MEASUREMENT_FIELDS = ['height', 'chest', 'waist']

export function validateMeasurements(measurements) {
  const missing = []
  for (const field of REQUIRED_MEASUREMENT_FIELDS) {
    const value = measurements?.[field]
    if (value == null || !Number.isFinite(Number(value)) || Number(value) <= 0) {
      missing.push(field)
    }
  }
  return missing
}

/** Returns true when inline checkout step can be skipped. */
export function isFulfillmentComplete(fulfillment, orderDetails = {}) {
  const shipping = buildShippingAddressFromFulfillment(fulfillment)
  const measurements = buildMeasurementsFromFulfillment(fulfillment, orderDetails)
  return (
    validateShippingAddress(shipping).length === 0 &&
    validateMeasurements(measurements).length === 0
  )
}

/** Field-level errors for inline checkout form. */
export function getFulfillmentFieldErrors(fulfillment, orderDetails = {}) {
  const errors = {}
  const shipping = buildShippingAddressFromFulfillment(fulfillment)
  const measurements = buildMeasurementsFromFulfillment(fulfillment, orderDetails)

  if (!shipping.name) errors.name = 'Name is required'
  if (!shipping.email) errors.email = 'Email is required'
  if (!shipping.street) errors.street = 'Street address is required'
  if (!shipping.city) errors.city = 'City is required'
  if (!shipping.state) errors.state = 'State is required'
  if (!shipping.zipCode) errors.zipCode = 'ZIP code is required'

  if (measurements.height == null) errors.height = 'Height is required'
  if (measurements.chest == null) errors.chest = 'Chest is required'
  if (measurements.waist == null) errors.waist = 'Waist is required'

  return errors
}

export function hasMissingFulfillment(fulfillment, orderDetails = {}) {
  return !isFulfillmentComplete(fulfillment, orderDetails)
}

export function buildMeasurementsFromUser(user, orderDetails = {}) {
  const num = (value) => {
    const n = Number(value)
    return Number.isFinite(n) ? n : undefined
  }

  return {
    size: orderDetails.size || 'M',
    chest: num(user?.chest),
    waist: num(user?.waist),
    hips: num(user?.hips),
    inseam: num(user?.inseam),
    height: num(user?.height),
    shoulder: num(user?.shoulder),
    sleeve: num(user?.sleeve),
    units: 'imperial'
  }
}

export function buildOrderDesignPayload(design, imageUrl, orderDetails = {}) {
  return {
    imageUrl,
    type: design?.type || 'Custom Garment',
    material: design?.material || '',
    materialVariant: design?.materialVariant || design?.specifications?.materialVariant || '',
    color: orderDetails.color || design?.color || '',
    style: design?.style || '',
    prompt:
      design?.prompt ||
      design?.description ||
      design?.specialInstructions ||
      design?.additionalNotes ||
      '',
    presentationMode: design?.presentationMode === 'on_model' ? 'on_model' : 'product'
  }
}

/**
 * Attach Gelato print files + blank metadata to an existing order.
 * NEW endpoint — does not change POST /api/orders.
 */
export async function attachGelatoFulfillment({
  orderId,
  garmentId,
  colorId,
  sizeId,
  productUid,
  printFiles,
  mockupImageUrl
}) {
  return postJson('/gelato/attach-fulfillment', {
    orderId,
    garmentId,
    colorId,
    sizeId,
    productUid,
    printFiles,
    mockupImageUrl
  })
}

/**
 * Fetch a live retail quote (server computes from Gelato).
 * Customer responses include retail only — cost/margin are admin-only.
 * @returns {Promise<object>} quote with retail / price
 */
export async function fetchPricingQuote({
  garmentId,
  colorId,
  sizeId,
  productUid = null,
  printSides = 'both',
  country = 'US',
  tier = 'budget',
  quantity = 1
}) {
  const headers = await getAuthHeaders()
  const params = new URLSearchParams({
    tier,
    garment: garmentId || '',
    color: colorId || '',
    size: sizeId || '',
    sides: printSides || 'both',
    country: normalizeCountryCode(country, 'US'),
    quantity: String(quantity || 1)
  })
  if (productUid) params.set('productUid', productUid)

  const response = await fetch(`${API_BASE_URL}/pricing/quote?${params}`, { headers })
  let payload = {}
  try {
    payload = await response.json()
  } catch {
    // ignore
  }
  if (!response.ok) {
    throw errorFromApi(payload, response.status, 'Pricing is temporarily unavailable. Please try again.')
  }
  return payload.quote || payload
}

const GELATO_POD_TIERS = new Set(['budget', 'mid', 'premium'])

function isGelatoPodTierClient(tier) {
  return GELATO_POD_TIERS.has(String(tier || '').toLowerCase())
}

/**
 * Create a pending order and start Stripe Checkout.
 * Optional gelatoFulfillment: after order create, attaches print files (Gelato POD).
 * For budget/mid/premium: server computes price — client expectedPrice is display-only.
 * @returns {Promise<{ orderId: string, checkoutUrl: string }>}
 */
export async function createOrderAndStartCheckout({
  design,
  imageUrl,
  measurements,
  shippingAddress,
  price,
  expectedPrice = null,
  manufacturingTier = null,
  gelatoFulfillment = null,
  adminBypass = false
}) {
  const tier =
    manufacturingTier ||
    design?.manufacturingTier ||
    (gelatoFulfillment ? 'budget' : 'luxury')
  const isPod = isGelatoPodTierClient(tier)
  // The server re-checks admin from Firestore; this only skips the local early exit.
  if (isPod && !PRINTED_CHECKOUT_ENABLED && !adminBypass) {
    throw new Error(PRINTED_CHECKOUT_PAUSED_MESSAGE)
  }

  const expected = Number(expectedPrice ?? price)
  if (!isPod && (!Number.isFinite(expected) || expected <= 0)) {
    throw new Error('Invalid order total.')
  }

  const missing = validateShippingAddress(shippingAddress)
  if (missing.length) {
    throw new Error(`Shipping information is incomplete (missing: ${missing.join(', ')}).`)
  }

  const measurementGaps = validateMeasurements(measurements)
  if (measurementGaps.length) {
    throw new Error(`Body measurements are incomplete (missing: ${measurementGaps.join(', ')}).`)
  }

  const orderDesign = buildOrderDesignPayload(design, imageUrl)
  if (!orderDesign.type) {
    throw new Error('Design is missing a garment type.')
  }

  const orderBody = {
    design: {
      ...orderDesign,
      manufacturingTier: tier,
      ...(gelatoFulfillment
        ? {
            garmentId: gelatoFulfillment.garmentId,
            colorId: gelatoFulfillment.colorId,
            sizeId: gelatoFulfillment.sizeId,
            productUid: gelatoFulfillment.productUid || null
          }
        : {})
    },
    measurements,
    shippingAddress,
    manufacturingTier: tier
  }

  if (isPod) {
    orderBody.gelato = {
      manufacturingTier: tier,
      garmentId: gelatoFulfillment?.garmentId || design?.garmentId,
      colorId: gelatoFulfillment?.colorId || design?.colorId,
      sizeId: gelatoFulfillment?.sizeId || design?.sizeId || measurements?.size,
      productUid: gelatoFulfillment?.productUid || design?.productUid || null,
      ...(gelatoFulfillment?.printSides ? { printSides: gelatoFulfillment.printSides } : {})
    }
    // Display hint only — server recomputes and never charges this
    if (Number.isFinite(expected) && expected > 0) {
      orderBody.expectedPrice = expected
    }
  } else {
    orderBody.price = expected
  }

  const orderResponse = await postJson('/orders', orderBody)

  const orderId = orderResponse.order?.orderId
  if (!orderId) {
    throw new Error('Order was created but no orderId was returned.')
  }

  // Budget / Gelato: attach print files via isolated endpoint (not part of /api/orders)
  if (gelatoFulfillment) {
    await attachGelatoFulfillment({
      orderId,
      garmentId: gelatoFulfillment.garmentId,
      colorId: gelatoFulfillment.colorId,
      sizeId: gelatoFulfillment.sizeId,
      productUid: gelatoFulfillment.productUid,
      printFiles: gelatoFulfillment.printFiles,
      mockupImageUrl: imageUrl
    })
  }

  const checkoutResponse = await postJson('/checkout', {
    orderId,
    // Keep Stripe return on the same host the shopper signed in on
    returnOrigin: typeof window !== 'undefined' ? window.location.origin : undefined
  })
  const checkoutUrl = checkoutResponse.checkoutUrl
  if (!checkoutUrl) {
    throw new Error('Checkout session was created but no payment URL was returned.')
  }

  return {
    orderId,
    checkoutUrl,
    sessionId: checkoutResponse.sessionId,
    price: orderResponse.order?.price
  }
}

/** The signed-in customer's orders, as the server shows them to customers. */
export async function fetchMyOrders() {
  const headers = await getAuthHeaders()
  const response = await fetch(`${API_BASE_URL}/orders`, { headers })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw errorFromApi(payload, response.status, "We couldn't load your orders. Please try again.")
  }
  return payload.orders || []
}

/** Fetch a single order (owner or admin). */
export async function fetchOrder(orderId) {
  const headers = await getAuthHeaders()
  const response = await fetch(`${API_BASE_URL}/orders/${encodeURIComponent(orderId)}`, {
    headers
  })
  let payload = {}
  try {
    payload = await response.json()
  } catch {
    // ignore
  }
  if (!response.ok) {
    throw errorFromApi(payload, response.status, "We couldn't load this order. Please try again.")
  }
  return payload.order || payload
}

/**
 * If webhook missed (common locally), ask the server to mark paid from Stripe session.
 */
export async function syncCheckoutPayment({ orderId, sessionId }) {
  return postJson('/checkout/sync', { orderId, sessionId })
}
